import { signal } from '@preact/signals';
import { useEffect } from 'preact/hooks';

import { Button, showToast } from '@amumin/ui';
import { Icon } from '@amumin/ui-core';

import { api } from '../api.js';
import {
  dismissPushResume,
  enablePush,
  rememberPushOptIn,
  resumePushAfterLogin,
} from '../lib/push.js';
import { appText, context } from '../state/session.js';

// Meldingen na het inloggen (nieuwe adressen, 28 sep 2026; lib/push.ts): stil opnieuw aanmelden
// als het kan, anders één rustige vraag bovenaan. Eén keer per login per browser.

/** Voor welke persoon we het na deze app-start al bekeken (niet elke navigatie opnieuw). */
let checkedFor: string | null = null;
const ask = signal<string | null>(null);
const busy = signal(false);
const denied = signal(false);

export function PushResumeBanner() {
  const who = context.value?.identity.id ?? null;

  useEffect(() => {
    if (!who || checkedFor === who) return;
    checkedFor = who;
    void resumePushAfterLogin(who)
      .then((d) => {
        if (d === 'ask' && context.value?.identity.id === who) ask.value = who;
      })
      .catch(() => undefined);
  }, [who]);

  if (!who || ask.value !== who) return null;
  const p = appText.value.domein.push;

  const later = () => {
    dismissPushResume(who);
    ask.value = null;
  };

  const turnOn = async () => {
    busy.value = true;
    try {
      const settings = await api.push.settings();
      if (!settings.vapid_public_key) {
        ask.value = null;
        return;
      }
      const res = await enablePush(settings.vapid_public_key);
      if (res === 'ok') {
        rememberPushOptIn(who);
        ask.value = null;
        showToast({ kind: 'success', message: p.done });
      } else if (res === 'denied') {
        denied.value = true;
      } else {
        ask.value = null;
      }
    } catch {
      ask.value = null;
    } finally {
      busy.value = false;
    }
  };

  return (
    <div class="banner push-resume" role="status" data-name="push-resume">
      <Icon id="bell" size={20} />
      <div>
        <strong>{p.title}</strong>
        <p class="small" style="margin:0">
          {denied.value ? p.denied : p.body}
        </p>
        <div class="push-resume-actions">
          {denied.value ? null : (
            <Button
              name="push-resume-on"
              variant="primary"
              busy={busy.value}
              onPress={() => void turnOn()}
            >
              {p.action}
            </Button>
          )}
          <Button name="push-resume-later" variant="ghost" onPress={later}>
            {p.later}
          </Button>
        </div>
      </div>
    </div>
  );
}
